import { useCallback, useState, useRef, useContext, useEffect } from 'react';
import type { TestMicrophoneReturn, TestSpeakerReturn, VideoPlayer } from '@zoom/videosdk';
// eslint-disable-next-line no-duplicate-imports
import ZoomVideo from '@zoom/videosdk';
import { useMount, useUnmount } from '../../hooks';
import './preview.scss';
import MicrophoneButton from '../video/components/microphone';
import CameraButton from '../video/components/camera';
import { Button, Progress, Select } from 'antd';
import type { MediaDevice } from '../video/video-types';
import classNames from 'classnames';
import ZoomContext from '../../context/zoom-context';
import type { RouteComponentProps } from 'react-router-dom';
import { useParams, useHistory } from 'react-router-dom';
import LoadingLayer from '../../component/loading-layer';
import mobileAppIcon from '../../component/webp/mobile_app.webp';
import PermissionRequestModal from './permission-request-modal';
import PermissionInstructionModal from './permission-instruction-modal';
import Header from '../../component/header/header';
import ControlBar from '../video/components/control-bar/control-bar';
import VideoView from '../../component/video-view/video-view';
import JoinPanel from '../../component/join-panel/join-panel';

// label: string;
// deviceId: string;
let prevMicFeedbackStyle = '';
let micFeedBackInteval: any = '';

let localAudio = ZoomVideo.createLocalAudioTrack();
let localVideo = ZoomVideo.createLocalVideoTrack();
let allDevices;

const mountDevices: () => Promise<{
  mics: MediaDevice[];
  speakers: MediaDevice[];
  cameras: MediaDevice[];
}> = async () => {
  allDevices = await ZoomVideo.getDevices();
  const cameraDevices: Array<MediaDeviceInfo> = allDevices.filter((device) => {
    return device.kind === 'videoinput';
  });
  const micDevices: Array<MediaDeviceInfo> = allDevices.filter((device) => {
    return device.kind === 'audioinput';
  });
  const speakerDevices: Array<MediaDeviceInfo> = allDevices.filter((device) => {
    return device.kind === 'audiooutput';
  });
  return {
    mics: micDevices.map((item) => {
      return { label: item.label, deviceId: item.deviceId };
    }),
    speakers: speakerDevices.map((item) => {
      return { label: item.label, deviceId: item.deviceId };
    }),
    cameras: cameraDevices.map((item) => {
      return { label: item.label, deviceId: item.deviceId };
    })
  };
};

const updateMicFeedbackStyle = () => {
  const newVolumeIntensity = localAudio.getCurrentVolume();
  let newMicFeedbackStyle = '';

  if (newVolumeIntensity === 0) {
    newMicFeedbackStyle = '';
  } else if (newVolumeIntensity <= 0.05) {
    newMicFeedbackStyle = 'mic-feedback__very-low';
  } else if (newVolumeIntensity <= 0.1) {
    newMicFeedbackStyle = 'mic-feedback__low';
  } else if (newVolumeIntensity <= 0.15) {
    newMicFeedbackStyle = 'mic-feedback__medium';
  } else if (newVolumeIntensity <= 0.2) {
    newMicFeedbackStyle = 'mic-feedback__high';
  } else if (newVolumeIntensity <= 0.25) {
    newMicFeedbackStyle = 'mic-feedback__very-high';
  } else {
    newMicFeedbackStyle = 'mic-feedback__max';
  }
  const micIcon: any = document.getElementById('auido-volume-feedback');
  if (prevMicFeedbackStyle !== '' && micIcon) {
    micIcon.classList.toggle(prevMicFeedbackStyle);
  }

  if (newMicFeedbackStyle !== '' && micIcon) {
    micIcon.classList.toggle(newMicFeedbackStyle);
  }
  prevMicFeedbackStyle = newMicFeedbackStyle;
};

const { Option } = Select;

// Helper function to get permission reset instructions based on browser
const getBrowserPermissionInstructions = (deviceName: string): string => {
  const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
  const isFirefox = /Firefox/.test(navigator.userAgent);
  const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
  const isEdge = /Edg/.test(navigator.userAgent);

  if (isChrome || isEdge) {
    return `To enable ${deviceName} access:\n1. Click the lock icon (i) in the address bar\n2. Find "${deviceName === 'microphone' ? 'Microphone' : deviceName === 'camera' ? 'Camera' : 'Microphone and Camera'}"\n3. Change it to "Allow"\n4. Refresh the page`;
  } else if (isFirefox) {
    return `To enable ${deviceName} access:\n1. Click the lock icon (🔒) in the address bar\n2. Click "More Information"\n3. Go to "Permissions" tab\n4. Find "${deviceName === 'microphone' ? 'Use the Microphone' : deviceName === 'camera' ? 'Use the Camera' : 'Use the Microphone and Camera'}"\n5. Change to "Allow"\n6. Refresh the page`;
  } else if (isSafari) {
    return `To enable ${deviceName} access:\n1. Go to Safari > Settings > Websites\n2. Find "${deviceName === 'microphone' ? 'Microphone' : deviceName === 'camera' ? 'Camera' : 'Microphone and Camera'}"\n3. Find this website and change to "Allow"\n4. Refresh the page`;
  } else {
    return `To enable ${deviceName} access, please check your browser settings and allow ${deviceName} access for this website, then refresh the page.`;
  }
};

interface PreviewContainerProps extends RouteComponentProps {
  previewProps: any;
}

const PreviewContainer: React.FunctionComponent<PreviewContainerProps> = (props) => {
  const { previewProps } = props;
  const { topic, token, password, name, subscriptionStatus, userRole } = previewProps;
  const isUseVideoPlayer = new URLSearchParams(props.location.search).get('useVideoPlayer') === '1';
  const [isStartedAudio, setIsStartedAudio] = useState<boolean>(false);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [isStartedVideo, setIsStartedVideo] = useState<boolean>(false);
  const [micList, setMicList] = useState<MediaDevice[]>([]);
  const [speakerList, setSpeakerList] = useState<MediaDevice[]>([]);
  const [cameraList, setCameraList] = useState<MediaDevice[]>([]);
  const [activeMicrophone, setActiveMicrophone] = useState('');
  const [activeSpeaker, setActiveSpeaker] = useState('');
  const [activeCamera, setActiveCamera] = useState('');
  const [outputLevel, setOutputLevel] = useState(0);
  const [inputLevel, setInputLevel] = useState(0);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const [isPlayingRecording, setIsPlayingRecording] = useState(false);
  const [isInVBMode, setIsInVBMode] = useState(false);
  const [isBlur, setIsBlur] = useState(false);
  const speakerTesterRef = useRef<TestSpeakerReturn>();
  const microphoneTesterRef = useRef<TestMicrophoneReturn>();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const videoPlayerRef = useRef<VideoPlayer | null>(null);
  const { slotBookingId, userId } = useParams<{ slotBookingId: string; userId: string }>();
  const history = useHistory();
  const zmClient = useContext(ZoomContext);
  const [isLoading, setIsLoading] = useState(false);
  const [csmRole, setCsmRole] = useState(userRole === 'CSM' ? true : false);
  // console.log('csmRole', csmRole);
  const [hasMicrophonePermission, setHasMicrophonePermission] = useState<boolean | null>(null);
  const [hasCameraPermission, setHasCameraPermission] = useState<boolean | null>(null);
  const [permissionModalVisible, setPermissionModalVisible] = useState(false);
  const [permissionModalType, setPermissionModalType] = useState<'microphone' | 'camera' | 'both'>('microphone');
  const [instructionModalVisible, setInstructionModalVisible] = useState(false);
  const [instructionDeviceName, setInstructionDeviceName] = useState('');
  const [instructionText, setInstructionText] = useState('');

  const userAgent = navigator.userAgent || navigator.vendor;
  const isAndroid = /android/i.test(userAgent);
  const isIphone = /iphone|ipad|ipod/i.test(userAgent);
  const isIpad = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  const isIOS = isIphone || isIpad;
  const isMobile = isAndroid || isIOS;

  const resetLocalAudio = useCallback(async () => {
    try {
      await localAudio?.stop();
    } catch (error) {
      console.warn('Failed to stop existing preview audio track:', error);
    }
    localAudio = ZoomVideo.createLocalAudioTrack();
    setIsStartedAudio(false);
    setIsMuted(false);
  }, []);

  const checkPermissions = useCallback(async () => {
    try {
      if (navigator.permissions && navigator.permissions.query) {
        try {
          const micPermission = await navigator.permissions.query({ name: 'microphone' as PermissionName });
          console.log('Microphone permission state:', micPermission.state);
          setHasMicrophonePermission(
            micPermission.state === 'granted' ? true : micPermission.state === 'denied' ? false : null
          );
          micPermission.onchange = () => {
            console.log('Microphone permission changed to:', micPermission.state);
            setHasMicrophonePermission(
              micPermission.state === 'granted' ? true : micPermission.state === 'denied' ? false : null
            );
          };
        } catch (e) {
          console.warn('Microphone permission query not supported:', e);
        }
      }
      if (navigator.permissions && navigator.permissions.query) {
        try {
          const cameraPermission = await navigator.permissions.query({ name: 'camera' as PermissionName });
          console.log('Camera permission state:', cameraPermission.state);
          setHasCameraPermission(
            cameraPermission.state === 'granted' ? true : cameraPermission.state === 'denied' ? false : null
          );
          cameraPermission.onchange = () => {
            console.log('Camera permission changed to:', cameraPermission.state);
            setHasCameraPermission(
              cameraPermission.state === 'granted' ? true : cameraPermission.state === 'denied' ? false : null
            );
          };
        } catch (e) {
          console.warn('Camera permission query not supported:', e);
        }
      }
    } catch (error) {
      console.warn('Error checking permissions:', error);
    }
  }, []);

  const requestPermission = useCallback(
    async (type: 'microphone' | 'camera' | 'both') => {
      console.log('Requesting permission for:', type);

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        console.error('getUserMedia is not supported in this browser');
        alert(
          'Your browser does not support media access. Please use a modern browser like Chrome, Firefox, or Safari.'
        );
        return;
      }

      let permissionAlreadyDenied = false;
      if (navigator.permissions && navigator.permissions.query) {
        try {
          if (type === 'microphone' || type === 'both') {
            const micPermission = await navigator.permissions.query({ name: 'microphone' as PermissionName });
            if (micPermission.state === 'denied') {
              permissionAlreadyDenied = true;
              console.log('Microphone permission already denied');
            }
          }
          if (type === 'camera' || type === 'both') {
            const cameraPermission = await navigator.permissions.query({ name: 'camera' as PermissionName });
            if (cameraPermission.state === 'denied') {
              permissionAlreadyDenied = true;
              console.log('Camera permission already denied');
            }
          }
        } catch (e) {
          console.warn('Could not check permission state:', e);
        }
      }

      if (permissionAlreadyDenied) {
        const deviceName =
          type === 'microphone' ? 'microphone' : type === 'camera' ? 'camera' : 'microphone and camera';
        const instructions = getBrowserPermissionInstructions(deviceName);

        setInstructionDeviceName(deviceName);
        setInstructionText(instructions);
        setInstructionModalVisible(true);
        setPermissionModalVisible(false);

        if (type === 'microphone' || type === 'both') {
          setHasMicrophonePermission(false);
        }
        if (type === 'camera' || type === 'both') {
          setHasCameraPermission(false);
        }
        return;
      }

      setPermissionModalVisible(false);

      try {
        const constraints: MediaStreamConstraints = {};
        if (type === 'microphone' || type === 'both') {
          constraints.audio = true;
        }
        if (type === 'camera' || type === 'both') {
          constraints.video = true;
        }

        console.log('Calling getUserMedia with constraints:', constraints);

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        console.log('Permission granted, stream received');

        stream.getTracks().forEach((track) => {
          track.stop();
          console.log('Track stopped:', track.kind);
        });

        if (type === 'microphone' || type === 'both') {
          setHasMicrophonePermission(true);
        }
        if (type === 'camera' || type === 'both') {
          setHasCameraPermission(true);
        }

        if (type === 'microphone' || type === 'both') {
          if (!isStartedAudio) {
            try {
              console.log('Starting audio after permission granted');
              await localAudio?.start();
              setIsStartedAudio(true);
            } catch (e) {
              console.warn('Failed to start audio after permission granted:', e);
            }
          }
        }
        if (type === 'camera' || type === 'both') {
          if (!isStartedVideo) {
            try {
              console.log('Starting video after permission granted');
              if (isUseVideoPlayer && videoPlayerRef.current) {
                await localVideo?.start(videoPlayerRef.current);
                setIsStartedVideo(true);
              } else if (videoRef.current) {
                await localVideo?.start(videoRef.current);
                setIsStartedVideo(true);
              }
            } catch (e) {
              console.warn('Failed to start video after permission granted:', e);
            }
          }
        }
      } catch (error: any) {
        console.error('Permission request failed:', error);
        console.error('Error name:', error.name);
        console.error('Error message:', error.message);

        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          console.log('Permission denied by user or blocked in browser settings');
          if (type === 'microphone' || type === 'both') {
            setHasMicrophonePermission(false);
          }
          if (type === 'camera' || type === 'both') {
            setHasCameraPermission(false);
          }

          const deviceName =
            type === 'microphone' ? 'microphone' : type === 'camera' ? 'camera' : 'microphone and camera';
          const instructions = getBrowserPermissionInstructions(deviceName);

          setInstructionDeviceName(deviceName);
          setInstructionText(instructions);
          setInstructionModalVisible(true);
        } else if (error.name === 'NotFoundError') {
          console.error('No media device found');
          alert('No media device found. Please connect a microphone or camera.');
        } else if (error.name === 'NotReadableError') {
          console.error('Media device is already in use');
          alert('Media device is already in use by another application.');
        } else {
          console.error('Unknown error:', error);
        }
      }
    },
    [isStartedAudio, isStartedVideo, isUseVideoPlayer]
  );

  const onCameraClick = useCallback(async () => {
    console.log('Camera click: ', isStartedVideo);
    if (!isStartedVideo && hasCameraPermission === false) {
      setPermissionModalType('camera');
      setPermissionModalVisible(true);
      return;
    }

    if (isStartedVideo) {
      await localVideo?.stop();
      setIsStartedVideo(false);
      setIsInVBMode(false);
      setIsBlur(false);
    } else {
      try {
        if (isUseVideoPlayer) {
          if (videoPlayerRef.current) {
            await localVideo?.start(videoPlayerRef.current);
            setIsStartedVideo(true);
            setHasCameraPermission(true);
          }
        } else {
          if (videoRef.current) {
            await localVideo?.start(videoRef.current);
            setIsStartedVideo(true);
            setHasCameraPermission(true);
          }
        }
      } catch (error: any) {
        console.warn('Camera start error:', error);
        if (
          error.name === 'NotAllowedError' ||
          error.name === 'PermissionDeniedError' ||
          error.type === 'INSUFFICIENT_PRIVILEGES'
        ) {
          setHasCameraPermission(false);
          setPermissionModalType('camera');
          setPermissionModalVisible(true);
        }
      }
    }
  }, [isStartedVideo, isUseVideoPlayer, hasCameraPermission]);

  const onMicrophoneClick = useCallback(async () => {
    // Check permission first
    if (!isStartedAudio && hasMicrophonePermission === false) {
      setPermissionModalType('microphone');
      setPermissionModalVisible(true);
      return;
    }

    if (isStartedAudio) {
      if (isMuted) {
        try {
          await localAudio?.unmute();
        } catch (error: any) {
          const text = String(error?.message ?? error?.name ?? error ?? '');
          if (/AudioAlreadyUnmutedError|already unmuted/i.test(text)) {
            console.warn('Audio already unmuted, syncing preview state:', error);
          } else {
            throw error;
          }
        }
        micFeedBackInteval = setInterval(updateMicFeedbackStyle, 500);
        setIsMuted(false);
      } else {
        try {
          await localAudio?.mute();
        } catch (error: any) {
          const text = String(error?.message ?? error?.name ?? error ?? '');
          if (/AudioAlreadyMutedError|already muted/i.test(text)) {
            console.warn('Audio already muted, syncing preview state:', error);
          } else {
            throw error;
          }
        }
        if (micFeedBackInteval) {
          clearInterval(micFeedBackInteval);
        }
        setIsMuted(true);
      }
    } else {
      try {
        await localAudio?.start();
        setIsStartedAudio(true);
        setHasMicrophonePermission(true);
      } catch (error: any) {
        console.warn('Microphone start error:', error);
        if (
          error.name === 'NotAllowedError' ||
          error.name === 'PermissionDeniedError' ||
          error.type === 'INSUFFICIENT_PRIVILEGES'
        ) {
          setHasMicrophonePermission(false);
          setPermissionModalType('microphone');
          setPermissionModalVisible(true);
        }
      }
    }
  }, [isStartedAudio, isMuted, hasMicrophonePermission]);

  const startAudio = async () => {
    console.log('startAudio');
    try {
      await localAudio?.start();
      setIsStartedAudio(true);
      setHasMicrophonePermission(true);
      try {
        await localAudio?.unmute();
      } catch (error: any) {
        const text = String(error?.message ?? error?.name ?? error ?? '');
        if (/AudioAlreadyUnmutedError|already unmuted/i.test(text)) {
          console.warn('Audio already unmuted after startAudio, syncing state:', error);
        } else {
          throw error;
        }
      }
      setIsMuted(false);
      micFeedBackInteval = setInterval(updateMicFeedbackStyle, 500);
      if (isUseVideoPlayer) {
        if (videoPlayerRef.current) {
          try {
            await localVideo?.start(videoPlayerRef.current);
            setIsStartedVideo(true);
            setHasCameraPermission(true);
          } catch (error: any) {
            console.warn('Video start error:', error);
            if (
              error.name === 'NotAllowedError' ||
              error.name === 'PermissionDeniedError' ||
              error.type === 'INSUFFICIENT_PRIVILEGES'
            ) {
              setHasCameraPermission(false);
            }
          }
        }
      } else {
        if (videoRef.current) {
          try {
            await localVideo?.start(videoRef.current);
            setIsStartedVideo(true);
            setHasCameraPermission(true);
          } catch (error: any) {
            console.warn('Video start error:', error);
            if (
              error.name === 'NotAllowedError' ||
              error.name === 'PermissionDeniedError' ||
              error.type === 'INSUFFICIENT_PRIVILEGES'
            ) {
              setHasCameraPermission(false);
            }
          }
        }
      }
    } catch (error: any) {
      console.warn('Audio start error:', error);
      if (
        error.name === 'NotAllowedError' ||
        error.name === 'PermissionDeniedError' ||
        error.type === 'INSUFFICIENT_PRIVILEGES'
      ) {
        setHasMicrophonePermission(false);
      }
    }
  };

  useEffect(() => {
    if (isMobile && !csmRole) {
      return;
    }
    checkPermissions();
    resetLocalAudio().then(() => {
      startAudio();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onMicrophoneMenuClick = async (key: string) => {
    const [type, deviceId] = key.split('|');
    if (type === 'microphone') {
      if (deviceId !== activeMicrophone) {
        await localAudio.stop();
        setIsMuted(true);
        localAudio = ZoomVideo.createLocalAudioTrack(deviceId);
        await localAudio.start();
        setActiveMicrophone(deviceId);
      }
    } else if (type === 'leave audio') {
      await localAudio.stop();
      setIsStartedAudio(false);
    }
  };
  const onSwitchCamera = async (key: string) => {
    if (localVideo) {
      if (activeCamera !== key) {
        await localVideo.switchCamera(key);
        setActiveCamera(key);
      }
    }
  };
  const onSwitchMicrophone = (key: string) => {
    setActiveMicrophone(key);
    if (speakerTesterRef.current) {
      speakerTesterRef.current.destroy();
      speakerTesterRef.current = undefined;
    }
    if (isRecordingVoice || isPlayingRecording) {
      microphoneTesterRef.current?.stop();
      setIsRecordingVoice(false);
      setIsPlayingRecording(false);
    }
  };
  const onSwitchSpeaker = (key: string) => {
    setActiveSpeaker(key);
    if (isPlayingAudio) {
      speakerTesterRef.current?.stop();
      setIsPlayingAudio(false);
      setOutputLevel(0);
    }
  };
  const onBlurBackground = useCallback(async () => {
    if (isInVBMode || isUseVideoPlayer) {
      if (isBlur) {
        await localVideo.updateVirtualBackground(undefined);
      } else {
        await localVideo.updateVirtualBackground('blur');
      }
      setIsBlur(!isBlur);
    } else {
      if (!isBlur) {
        await localVideo.stop();
        if (canvasRef.current) {
          localVideo.start(canvasRef.current, { imageUrl: 'blur' });
        }
        setIsInVBMode(true);
        setIsBlur(!isBlur);
      }
    }
  }, [isInVBMode, isBlur, isUseVideoPlayer]);
  useMount(() => {
    if (isMobile && !csmRole) {
      return;
    }
    console.log('mountDevices');
    mountDevices().then((devices) => {
      setMicList(devices.mics);
      setCameraList(devices.cameras);
      setSpeakerList(devices.speakers);
      if (devices.speakers.length > 0) {
        setActiveSpeaker(devices.speakers[0].deviceId);
      }
      if (devices.mics.length > 0) {
        setActiveMicrophone(devices.mics[0].deviceId);
      }
    });
  });
  const onTestSpeakerClick = () => {
    if (microphoneTesterRef.current) {
      microphoneTesterRef.current.destroy();
      microphoneTesterRef.current = undefined;
      setIsRecordingVoice(false);
      setIsPlayingRecording(false);
    }
    if (isPlayingAudio) {
      speakerTesterRef.current?.stop();
      setIsPlayingAudio(false);
      setOutputLevel(0);
    } else {
      speakerTesterRef.current = localAudio.testSpeaker({
        speakerId: activeSpeaker,
        onAnalyseFrequency: (value) => {
          setOutputLevel(Math.min(100, value));
        }
      });
      setIsPlayingAudio(true);
    }
  };
  const onTestMicrophoneClick = () => {
    if (speakerTesterRef.current) {
      speakerTesterRef.current.destroy();
      speakerTesterRef.current = undefined;
      setIsPlayingAudio(false);
    }
    if (!isPlayingRecording && !isRecordingVoice) {
      if (microphoneTesterRef.current && microphoneTesterRef.current?.stop) {
        microphoneTesterRef.current.stop();
        microphoneTesterRef.current.destroy();
      }
      microphoneTesterRef.current = localAudio.testMicrophone({
        microphoneId: activeMicrophone,
        speakerId: activeSpeaker,
        recordAndPlay: true,
        onAnalyseFrequency: (value) => {
          setInputLevel(Math.min(100, value));
        },
        onStartRecording: () => {
          setIsRecordingVoice(true);
        },
        onStartPlayRecording: () => {
          setIsRecordingVoice(false);
          setIsPlayingRecording(true);
        },
        onStopPlayRecording: () => {
          setIsPlayingRecording(false);
        }
      });
    } else if (isRecordingVoice) {
      microphoneTesterRef.current?.stopRecording();
      setIsRecordingVoice(false);
    } else if (isPlayingRecording) {
      microphoneTesterRef.current?.stop();
      setIsPlayingRecording(false);
    }
  };
  let microphoneBtn = 'Test';
  if (isRecordingVoice) {
    microphoneBtn = 'Recording';
  } else if (isPlayingRecording) {
    microphoneBtn = 'Playing';
  }
  useUnmount(() => {
    if (isMobile && !csmRole) {
      return;
    }
    if (isStartedAudio) {
      localAudio.stop();
    }
    if (isStartedVideo) {
      localVideo.stop();
    }
  });

  const onJoinMeeting = (event: any) => {
    document.documentElement.requestFullscreen();
    event.preventDefault();
    localStorage.setItem('subscriptionStatus', subscriptionStatus);
    setIsLoading(true);
    console.log('joining meeting', topic, token, name, password);
    zmClient
      .join(topic, token, name, password)
      .then(() => {
        history.push(`/meeting/${slotBookingId}/${userId}`, {
          isStartedAudio,
          isStartedVideo,
          isMuted,
          hasMicrophonePermission,
          hasCameraPermission,
          activeMicrophone,
          activeCamera,
          activeSpeaker
        });
      })
      .catch((e) => {
        console.log('Error joining meeting', e);
        alert(
          'Error joining meeting: ' +
            e.reason +
            '\nThe page will reload automatically. Please try joining again afterwards.'
        );
        // Reload the page
        window.location.reload();
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleAppStore = (url: string) => {
    window.open(url, '_self');
  };

  if (isMobile && !csmRole) {
    return (
      <div className="mobile-container">
        <img src={mobileAppIcon} alt="FlexifyMe App" className="app-icon" />
        <div className="app-name">Online Physiotherapy & Yoga</div>
        <div className="message">To join the sessions, please download App and join from App</div>

        {isAndroid && (
          <div className="store-container">
            <button
              className="app-store-icon"
              onClick={() => handleAppStore('https://play.google.com/store/apps/details?id=com.flexifymeapp')}
            >
              Download from Google Play
            </button>
          </div>
        )}
        {isIOS && (
          <div className="store-container">
            <button
              className="app-store-icon"
              onClick={() => handleAppStore('https://apps.apple.com/in/app/flexifyme/id6443768113')}
            >
              Download from App Store
            </button>
          </div>
        )}
        {!isAndroid && !isIOS && (
          <div className="store-container">
            <button
              className="app-store-icon"
              onClick={() => handleAppStore('https://play.google.com/store/apps/details?id=com.flexifymeapp')}
            >
              Download from Google Play
            </button>
            <button
              className="app-store-icon"
              onClick={() => handleAppStore('https://apps.apple.com/in/app/flexifyme/id6443768113')}
            >
              Download from App Store
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      {isLoading && <LoadingLayer content="Joining the session..." />}
      <div className="js-preview-view">
        <Header userName={name} email="" />

        <div className="content">
          <VideoView
            videoRef={videoRef}
            canvasRef={canvasRef}
            videoPlayerRef={videoPlayerRef}
            isInVBMode={isInVBMode}
            isUseVideoPlayer={isUseVideoPlayer}
            userName={name}
            isStartedVideo={isStartedVideo}
          >
            <MicrophoneButton
              isStartedAudio={isStartedAudio}
              isMuted={isMuted}
              isMicrophoneForbidden={hasMicrophonePermission === false}
              onMicrophoneClick={onMicrophoneClick}
              onMicrophoneMenuClick={onMicrophoneMenuClick}
              activeMicrophone={activeMicrophone}
              activeSpeaker={activeSpeaker}
            />
            <CameraButton
              isStartedVideo={isStartedVideo}
              isCameraForbidden={hasCameraPermission === false}
              onCameraClick={onCameraClick}
              onSwitchCamera={onSwitchCamera}
              onBlurBackground={onBlurBackground}
              cameraList={cameraList}
              activeCamera={activeCamera}
              isBlur={isBlur}
              isPreview={true}
            />
            {(hasMicrophonePermission === false || hasCameraPermission === false) && (
              <div className="permission-error-message">
                <p className="permission-error-text">
                  Please enable access to your{' '}
                  {hasMicrophonePermission === false && hasCameraPermission === false
                    ? 'microphone and camera'
                    : hasMicrophonePermission === false
                      ? 'microphone'
                      : 'camera'}
                </p>
                <a
                  href="#"
                  className="permission-error-link"
                  onClick={(e) => {
                    e.preventDefault();
                    if (hasMicrophonePermission === false && hasCameraPermission === false) {
                      setPermissionModalType('both');
                    } else if (hasMicrophonePermission === false) {
                      setPermissionModalType('microphone');
                    } else {
                      setPermissionModalType('camera');
                    }
                    setPermissionModalVisible(true);
                  }}
                >
                  {hasMicrophonePermission === false && hasCameraPermission === false
                    ? 'Enable microphone and camera'
                    : hasMicrophonePermission === false
                      ? 'Enable microphone'
                      : 'Enable camera'}
                </a>
              </div>
            )}
          </VideoView>
          <JoinPanel onClickJoin={onJoinMeeting} />
        </div>

        <ControlBar
          isPlayingAudio={isPlayingAudio}
          inputLevel={inputLevel}
          speakerList={speakerList}
          activeSpeaker={activeSpeaker}
          outputLevel={outputLevel}
          activeMicrophone={activeMicrophone}
          microphoneBtn={microphoneBtn}
          micList={micList}
          onSwitchSpeaker={onSwitchSpeaker}
          onSwitchMicrophone={onSwitchMicrophone}
          onTestMicrophoneClick={onTestMicrophoneClick}
          onTestSpeakerClick={onTestSpeakerClick}
        />
      </div>

      <PermissionRequestModal
        visible={permissionModalVisible}
        type={permissionModalType}
        onRequestPermission={() => requestPermission(permissionModalType)}
        onCancel={() => setPermissionModalVisible(false)}
      />
      <PermissionInstructionModal
        visible={instructionModalVisible}
        deviceName={instructionDeviceName}
        instructions={instructionText}
        isChrome={
          (/Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor)) || /Edg/.test(navigator.userAgent)
        }
        onClose={() => setInstructionModalVisible(false)}
      />
    </>
  );
};

export default PreviewContainer;
